import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../../utils/formatDate";

const ProductionDailyActivityList = ({
    activities = [],
    loading,
    onEdit,
    onDelete
}) => {

    // ============================================================
    // SEARCH
    // ============================================================
    const [searchTerm, setSearchTerm] = useState("");

    // ============================================================
    // SORTING
    // ============================================================
    const [sortOrder, setSortOrder] = useState("asc");

    // ============================================================
    // PAGINATION
    // ============================================================
   const [currentPage, setCurrentPage] = useState(1);
const [recordsPerPage, setRecordsPerPage] = useState(5);

    // ============================================================
    // SEARCH + SORT
    // ============================================================
    const filteredAndSortedActivities = useMemo(() => {

        // ----------------------------
        // SEARCH
        // ----------------------------
        let result = activities.filter((activity) => {

            const search = searchTerm.toLowerCase().trim();

            if (!search) {
                return true;
            }

            return (
                String(activity.id ?? "")
                    .toLowerCase()
                    .includes(search) ||

                String(activity.activity_name ?? "")
                    .toLowerCase()
                    .includes(search) ||

                String(activity.plan_date ?? "")
                    .toLowerCase()
                    .includes(search) ||

                String(activity.planned_man ?? "")
                    .toLowerCase()
                    .includes(search) ||

                String(activity.actual_man ?? "")
                    .toLowerCase()
                    .includes(search) ||

                String(activity.shift_name ?? "")
                    .toLowerCase()
                    .includes(search) ||

                String(activity.remark ?? "")
                    .toLowerCase()
                    .includes(search)
            );
        });

        // ----------------------------
        // SORT BY ACTIVITY / PROCESS
        // ----------------------------
        result.sort((a, b) => {

            const activityA = String(
                a.activity_name ?? ""
            ).toLowerCase();

            const activityB = String(
                b.activity_name ?? ""
            ).toLowerCase();

            const comparison = activityA.localeCompare(
                activityB,
                undefined,
                {
                    numeric: true,
                    sensitivity: "base"
                }
            );

            return sortOrder === "asc"
                ? comparison
                : -comparison;
        });

        return result;

    }, [activities, searchTerm, sortOrder]);

    // ============================================================
    // RESET PAGE WHEN SEARCH / SORT CHANGES
    // ============================================================
        useEffect(() => {
            setCurrentPage(1);
        }, [searchTerm, sortOrder, recordsPerPage]);

    // ============================================================
    // PAGINATION CALCULATIONS
    // ============================================================
    const totalRecords = filteredAndSortedActivities.length;

    const totalPages = Math.ceil(
        totalRecords / recordsPerPage
    );

    const indexOfLastRecord =
        currentPage * recordsPerPage;

    const indexOfFirstRecord =
        indexOfLastRecord - recordsPerPage;

    const currentActivities =
        filteredAndSortedActivities.slice(
            indexOfFirstRecord,
            indexOfLastRecord
        );

    // ============================================================
    // SORT HANDLER
    // ============================================================
    const handleActivitySort = () => {

        setSortOrder((previousOrder) =>
            previousOrder === "asc"
                ? "desc"
                : "asc"
        );
    };

    // ============================================================
    // PAGE CHANGE
    // ============================================================
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // ============================================================
    // LOADING
    // ============================================================
    if (loading) {
        return (
            <div className="text-center py-5">

                <div className="spinner-border"></div>

                <div className="mt-1">
                    Loading activities...
                </div>

            </div>
        );
    }

    return (
        <div className="card shadow-sm">

            <div className="card-body">

                {/* =====================================================
                    SEARCH
                ====================================================== */}

                <div className="row mb-1">

                    <div className="col-md-4">

                        <label className="form-label fw-semibold">
                            Search Activities
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search Process / Activity..."
                            value={searchTerm}
                            onChange={(e) =>
                                setSearchTerm(e.target.value)
                            }
                        />

                    </div>

                    <div className="col-md-6 d-flex align-items-end justify-content-md-end mt-1 mt-md-0">

                        <div className="text-muted">
                            Showing{" "}
                            <strong>
                                {totalRecords}
                            </strong>{" "}
                            records
                        </div>

                    </div>

                </div>

                {/* =====================================================
                    TABLE
                ====================================================== */}

                <div className="table-responsive">

                    <table className="table table-bordered table-hover align-middle">

                        <thead className="table-dark">

                            <tr>

                                <th>
                                    ID
                                </th>

                                {/* =================================================
                                    SORTABLE ACTIVITY / PROCESS
                                ================================================= */}

                                <th
                                    onClick={handleActivitySort}
                                    style={{
                                        cursor: "pointer",
                                        userSelect: "none"
                                    }}
                                    title="Sort by Activity / Process"
                                >
                                    Activity/Process{" "}

                                    {sortOrder === "asc" ? (
                                        <span>▲</span>
                                    ) : (
                                        <span>▼</span>
                                    )}

                                </th>

                                <th>
                                    Plan Date
                                </th>

                                <th>
                                    Planned ManPower
                                </th>

                                <th>
                                    Actual ManPower
                                </th>

                                <th>
                                    Shift
                                </th>

                                <th>
                                    Remark
                                </th>

                                <th>
                                    Actions
                                </th>

                            </tr>

                        </thead>

                        <tbody>

                            {currentActivities.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="8"
                                        className="text-center"
                                    >
                                        {searchTerm
                                            ? "No matching activities found."
                                            : "No activities found."
                                        }
                                    </td>

                                </tr>

                            ) : (

                                currentActivities.map((activity) => (

                                    <tr key={activity.id}>

                                        <td>
                                            {activity.id}
                                        </td>

                                        <td>
                                            {activity.activity_name ?? "-"}
                                        </td>

                                        <td>
                                            {activity.plan_date
                                                ? formatDate(
                                                    activity.plan_date
                                                )
                                                : "-"
                                            }
                                        </td>

                                        <td>
                                            {activity.planned_man ?? "-"}
                                        </td>

                                        <td>
                                            {activity.actual_man ?? "-"}
                                        </td>

                                        <td>
                                            {activity.shift_name ?? "-"}
                                        </td>

                                        <td>
                                            {activity.remark ?? "-"}
                                        </td>

                                        <td>

                                            <div className="d-flex gap-1">

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-warning"
                                                    onClick={() =>
                                                        onEdit(activity)
                                                    }
                                                >
                                                    Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() =>
                                                        onDelete(activity)
                                                    }
                                                >
                                                    Delete
                                                </button>

                                            </div>

                                        </td>

                                    </tr>

                                ))

                            )}

                        </tbody>

                    </table>

                </div>

                {/* =====================================================
                    PAGINATION
                ====================================================== */}

{/* =====================================================
    PAGINATION
====================================================== */}

{totalRecords > 0 && (

    <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap gap-1">

        {/* =================================================
            LEFT SIDE - PAGE SIZE + RECORD COUNT
        ================================================= */}

        <div className="d-flex align-items-center gap-1">

            {/* PAGE SIZE */}

            <div className="d-flex align-items-center gap-1">

                <label
                    htmlFor="pageSize"
                    className="mb-0 text-muted"
                >
                    Show
                </label>

                <select
                    id="pageSize"
                    className="form-select form-select-sm"
                    style={{ width: "80px" }}
                    value={recordsPerPage}
                    onChange={(e) =>
                        setRecordsPerPage(
                            Number(e.target.value)
                        )
                    }
                >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                </select>

                <span className="text-muted">
                    entries
                </span>

            </div>

            {/* RECORD COUNT */}

            <div className="text-muted">

                Showing{" "}

                <strong>
                    {indexOfFirstRecord + 1}
                </strong>

                {" - "}

                <strong>
                    {Math.min(
                        indexOfLastRecord,
                        totalRecords
                    )}
                </strong>

                {" of "}

                <strong>
                    {totalRecords}
                </strong>

                {" records"}

            </div>

        </div>

        {/* =================================================
            RIGHT SIDE - PAGE NAVIGATION
        ================================================= */}

        {totalPages > 1 && (

            <nav>

                <ul className="pagination mb-0">

                    {/* PREVIOUS */}

                    <li
                        className={`page-item ${
                            currentPage === 1
                                ? "disabled"
                                : ""
                        }`}
                    >

                        <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                                setCurrentPage(
                                    currentPage - 1
                                )
                            }
                            disabled={
                                currentPage === 1
                            }
                        >
                            Previous
                        </button>

                    </li>

                    {/* PAGE NUMBERS */}

                    {Array.from(
                        {
                            length: totalPages
                        },
                        (_, index) => index + 1
                    ).map((page) => (

                        <li
                            key={page}
                            className={`page-item ${
                                currentPage === page
                                    ? "active"
                                    : ""
                            }`}
                        >

                            <button
                                type="button"
                                className="page-link"
                                onClick={() =>
                                    handlePageChange(page)
                                }
                            >
                                {page}
                            </button>

                        </li>

                    ))}

                    {/* NEXT */}

                    <li
                        className={`page-item ${
                            currentPage === totalPages
                                ? "disabled"
                                : ""
                        }`}
                    >

                        <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                                setCurrentPage(
                                    currentPage + 1
                                )
                            }
                            disabled={
                                currentPage ===
                                totalPages
                            }
                        >
                            Next
                        </button>

                    </li>

                </ul>

            </nav>

        )}

    </div>

)}


            </div>

        </div>
    );
};

export default ProductionDailyActivityList;

